import { useCallback } from 'react';

/**
 * Custom hook for extracting coordinates from table row data
 * Handles various coordinate formats with clean, consistent logic
 */
export const useCoordinateExtraction = () => {
  /**
   * Parse coordinate value from various formats
   */
  const parseCoordinateValue = (value: any): number | null => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const cleaned = value.trim();
      const parsed = parseFloat(cleaned);

      if (!isNaN(parsed)) {
        return parsed;
      }
    }

    return null;
  };

  const extractCoordinates = useCallback(
    (row: any): [number, number] | null => {
      const keys = Object.keys(row);
      let latKey: string | undefined;
      let lonKey: string | undefined;

      // Find latitude key (prioritize rawlat, then lat)
      latKey = keys.find((key) => key.toLowerCase() === 'rawlat');
      if (!latKey) {
        latKey = keys.find((key) => key.toLowerCase() === 'lat');
      }
      if (!latKey) {
        latKey = keys.find(
          (key) =>
            key.toLowerCase().includes('latitude') ||
            (key.toLowerCase().includes('lat') &&
              !key.toLowerCase().includes('lon'))
        );
      }

      // Find longitude key (prioritize rawlon, then lon)
      lonKey = keys.find((key) => key.toLowerCase() === 'rawlon');
      if (!lonKey) {
        lonKey = keys.find((key) => key.toLowerCase() === 'lon');
      }
      if (!lonKey) {
        lonKey = keys.find(
          (key) =>
            key.toLowerCase().includes('longitude') ||
            key.toLowerCase().includes('lng') ||
            (key.toLowerCase().includes('lon') &&
              !key.toLowerCase().includes('lat'))
        );
      }

      // If we found both keys, parse their values
      if (latKey && lonKey) {
        const lat = parseCoordinateValue(row[latKey]);
        const lon = parseCoordinateValue(row[lonKey]);

        if (lat !== null && lon !== null) {
          return [lon, lat];
        }
      }

      // Fallback: look for combined position field
      const posKey = keys.find((key) => {
        const lower = key.toLowerCase();
        return (
          lower === 'position' || lower === 'coords' || lower === 'coordinates'
        );
      });

      if (posKey && row[posKey]) {
        const posValue = row[posKey];

        if (typeof posValue === 'string') {
          // Try to parse "lat,lon" or "lat lon" format
          const coords = posValue.split(/[,\s]+/).map(Number);
          if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
            // Assume string format is "lat,lon" (most common)
            const [lon, lat] = coords;
            return [lon, lat];
          }
        } else if (Array.isArray(posValue) && posValue.length === 2) {
          const [lon, lat] = posValue;
          return [lon, lat];
        } else if (
          typeof posValue === 'object' &&
          posValue.lat &&
          posValue.lon
        ) {
          const lat = posValue.lat;
          const lon = posValue.lon;
          return [lon, lat];
        }
      }

      return null;
    },
    []
  );

  /**
   * Check if the given row data contains position information
   */
  const hasPositionData = useCallback((row: any): boolean => {
    const keys = Object.keys(row);
    return keys.some((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes('lat') ||
        lower.includes('lon') ||
        lower.includes('longitude') ||
        lower.includes('latitude') ||
        lower.includes('position') ||
        lower === 'coords' ||
        lower === 'coordinates'
      );
    });
  }, []);

  return {
    extractCoordinates,
    hasPositionData,
  };
};
